// ÁTTEKINTÉS adat-réteg: a MEGLÉVŐ lista-végpontokból állítja össze a kezdőlap widgetjeit — nincs új
// BFF-végpont és nincs második igazság-forrás.
//
// MIÉRT ÍGY: a kezdőlap widgetje pontosan egy lista-végpont első néhány sora vagy a darabszám-összesítője. Egy
// külön dashboard-aggregátor endpoint ugyanezt kérdezné, viszont DUPLIKÁLNÁ a szűrés/rendezés szabályait (mit
// jelent „aktuális", mit „lejárt") — a hiba pedig ott csendes lenne.
//
// CSEMPE-KAPU: a testreszabható csempék lekérdezése csak akkor fut, ha a csempe KI VAN RAKVA (`placed`) ÉS jogosult — a
// levett csempe hálózati hívást sem indít. Kivétel a "Rám vár" (fix blokk) forrásai: jegy · megbeszélés · eszköz-igény ·
// piszkozat — azok a csempe nélkül is futnak.
//
// JOG-KAPU: minden lekérdezés a saját jogához kötött `enabled`-del indul — jog nélkül a kezdőlap SOSEM kér
// olyat, amire 403 járna (nem a válaszból derül ki, hogy nem szabad). A widgetek ugyanezt a jogot nézik, tehát
// jog nélkül a kártya sem renderel (nem „tiltott" állapotot mutatunk, hanem a kártya nincs ott).
//
// TAKARÉKOSSÁG: ahol csak DARABSZÁM kell, `limit: 1`-gyel kérünk (a `total`/`groups` a válasz része). A jegyek
// EGY hívásból adják az aktív-összeset (`groups.in_progress`) ÉS a rám-osztottakat (`mineGroups`) — nincs két kör.
//
// KIVEZETVE (user-döntés): a „Legutóbbi közlemény" widget — az üzenetek külön lapon élnek, a
// friss üzenetről pedig az ÉRTESÍTÉS (harang) szól.
// HELYETTE (user-döntés, T15): a KITŰZÖTT közlemény — nem az automatikus „legutóbbi", hanem amit egy kezelő
// SZÁNDÉKOSAN kitűzött; és csak akkor van kártya, ha van ilyen (nincs üres állapot, nincs ismételt zaj).
import { FINANCE_PERMISSIONS, hasAnyPermission } from '@atlas/shared';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';
import { useMemo } from 'react';

import { api } from '@/lib/api';
import { useTimeZone } from '@/lib/format-datetime';

/** A kezdőlap ennyi kitűzött közleményt mutat (a Slack / Teams kitűzött-mintája: néhány elfér, több már zaj). */
export const PINNED_LIMIT = 3;

/** A widget-sorhoz szükséges megbeszélés-mezők (a lista-szerződés szűkítése — a widget nem lát többet). */
export interface DashboardMeeting {
  id: string;
  title: string;
  locationKey: string | null;
  startsAt: string | null;
  endsAt: string | null;
  participants: {
    id: string;
    /** `null` = az identity nem oldható fel (törölt személy) → „Törölt felhasználó" címke. */
    name: string | null;
    givenName: string | null;
    familyName: string | null;
    avatarUrl: string | null;
    /** A kolléga csapat-színe — az avatar háttere (partnernél null). */
    teamColor: string | null;
    status: string;
  }[];
}

/** Hány megbeszélés-sort mutat a widget (a kezdőlap nem lista-oldal). */
const MEETING_LIMIT = 5;
/** Hány sort olvasunk be az összeg-számoláshoz (lejárt számlák) — a lejárt halmaz kis nagyságrendű. */
const MONEY_ROW_LIMIT = 200;

/** Egy „rám vár" tétel: darabszám + a szűrt lista, ahol elvégezhető. */
export interface WorkItem {
  key: 'myTickets' | 'rsvp' | 'assetRequests' | 'draftInvoices';
  count: number;
  /** A cél-útvonal a MÁR SZŰRT listára (a kezdőlapról egy kattintás a munkáig). */
  to: string;
  // CSAK szöveg: a cél-lapok search-sémája (`validateSearch`) szöveget vár, és egy logikai érték az
  // URL-ben (`?mine=true`) a cél-lapot a router hibalapjára dobta — a típus ezt fordításkor megfogja
  search?: Record<string, string>;
}

/** Pénz-összesítő egy státuszra: darab + deviza-bontású összeg (több deviza esetén több sor). */
export interface MoneySummary {
  count: number;
  totals: { currency: string; amount: number }[];
}

/** „Figyelmet kér" adat: a csendben elromló dolgok (lejáró kártya · elmaradt usage-lekérés · hamarosan induló
 *  függő hozzárendelés · nyitott generálási kihagyás). */
export interface AttentionData {
  /** Hány bankkártya jár le a következő 60 napban (a lejártakat is beleértve). */
  expiringCards: number;
  /** `true` → az AKTUÁLIS hónapra egyetlen usage-sor sincs (a lekérés nem futott le). */
  usageMissing: boolean;
  /** Melyik hónap (a felirathoz): `YYYY-MM`. */
  usageMonth: string;
  /** Hány FÜGGŐ hozzárendelés indul hamarosan (a hozzárendelés-lista figyelem-paneljének ugyanaz a szabálya). */
  pendingStartingSoon: number;
  /** Hány NYITOTT generálási kihagyás van a JELEN hónapban (a számla-generátor kihagyás-naplója). */
  generationSkipsOpen: number;
}

export interface DashboardData {
  meetings: DashboardMeeting[] | null;
  meetingsLoading: boolean;
  /** A „rám vár" tételek (csak azok, amikre van jog); a nulla-darabszámú is benne van — a kártya szűri. */
  workItems: WorkItem[];
  workLoading: boolean;
  /** Nyitott jegyek ÖSSZESEN (KPI) — `null`, ha nincs jog vagy még töltünk. */
  openTickets: number | null;
  /** Pénzügy-kártya: lejárt · fizetésre váró · e hónapban kiállított. */
  finance: {
    overdue: MoneySummary;
    awaiting: MoneySummary;
    issuedThisMonth: number;
  } | null;
  financeLoading: boolean;
  /** MRR-KPI: érték + változás + 12 hónap trend (a sparkline-hoz). */
  revenue: {
    mrr: string;
    currency: string;
    changePct: number | null;
    trend: { month: string; amount: string }[];
  } | null;
  revenueLoading: boolean;
  /** „Figyelmet kér" kártya — `null`, ha egyik jog sincs meg. */
  attention: AttentionData | null;
  attentionLoading: boolean;
  /** A legutóbb KITŰZÖTT körüzenet (a saját archiváltjaim nélkül) — `null`: nincs ilyen, vagy nincs jog. */
  /** A nekem szóló kitűzött közlemények (legfeljebb `PINNED_LIMIT`, a legutóbb kitűzött elöl). */
  pinnedMessages: DashboardPinnedMessage[];
  /** Az ÖSSZES nekem szóló kitűzött darabszáma (a „+N további" linkhez). */
  pinnedTotal: number;
}

/** A kitűzött közlemény kártyájának adata. */
export interface DashboardPinnedMessage {
  id: string;
  subject: string;
  senderName: string | null;
  senderGivenName: string | null;
  senderFamilyName: string | null;
  senderAvatarUrl: string | null;
  senderTeamColor: string | null;
  pinnedAt: string;
  readByMe: boolean;
}

/** A widget-jogok egy helyen — a hívó (a route) a `can()`-ből tölti, a hook nem ismeri a jog-forrást. */
export interface DashboardPerms {
  meetings: boolean;
  tickets: boolean;
  /** `asset_requests.decide` ÉS `asset_inventory.view` — a sor az `/assets` listából él, ami az utóbbit kéri. */
  assetRequests: boolean;
  invoicesView: boolean;
  /** `invoices.manage` ÉS `invoices.view` — a piszkozat-sor a `/invoices` listából él, ami az utóbbit kéri. */
  invoicesManage: boolean;
  /** `usage_data.view` — a „figyelmet kér" kártya „erre a hónapra nincs usage lekérve" sora. */
  usageData: boolean;
  revenue: boolean;
  /** `payment_cards.view` — a „figyelmet kér" kártya lejáró-kártya sora. */
  paymentCards: boolean;
  /** `service_assignments.view` — a „figyelmet kér" kártya hamarosan induló, függő hozzárendelés sora. */
  serviceAssignments: boolean;
  /** `messages.view` — a kitűzött közlemény kártyája. */
  messages: boolean;
}

/** `YYYY-MM-DD` a helyi naptár szerint (a nap-pontos mezők zóna-konverzió NÉLKÜL hasonlítódnak). */
function localDay(d: Date): string {
  const m = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

/** Deviza-bontású összegzés a sorokból (a `grossTotal` string — a pontosság miatt csak itt lesz szám). */
function sumByCurrency(
  rows: { currency: string; grossTotal: string | null }[],
): MoneySummary['totals'] {
  const map = new Map<string, number>();
  for (const r of rows) {
    if (r.grossTotal === null) continue;
    const value = Number.parseFloat(r.grossTotal);
    if (!Number.isFinite(value)) continue;
    map.set(r.currency, (map.get(r.currency) ?? 0) + value);
  }
  return [...map.entries()]
    .map(([currency, amount]) => ({ currency, amount }))
    .sort((a, b) => b.amount - a.amount);
}

/**
 * A kezdőlap jog-térképe a `can`-ből. A „Rám vár" két sora a kapuját az ADAT-végpont jogával együtt kéri (a BFF-handler
 * `@RequirePermissions`-ével egyezően): enélkül a lekérdezés 403-at kapna, a sor linkje pedig zsákutca lenne.
 */
export function dashboardPerms(can: (permission: string) => boolean): DashboardPerms {
  return {
    meetings: can('meetings.view'),
    tickets: can('tickets.view'),
    assetRequests: can('asset_requests.decide') && can('asset_inventory.view'),
    invoicesView: can('invoices.view'),
    invoicesManage: can('invoices.manage') && can('invoices.view'),
    usageData: can('usage_data.view'),
    revenue: hasAnyPermission(FINANCE_PERMISSIONS.revenueView, can),
    paymentCards: can('payment_cards.view'),
    serviceAssignments: can('service_assignments.view'),
    messages: can('messages.view'),
  };
}

export function useDashboardData(
  perms: DashboardPerms,
  /** A kirakott csempék id-i — a nem kirakott csempe lekérdezése nem indul. */
  placed: ReadonlySet<string>,
): DashboardData {
  const financePlaced = perms.invoicesView && placed.has('finance');
  const attentionPlaced = placed.has('attention');
  // AKTUÁLIS megbeszélések: a MAI nap 00:00-tól előre, kezdés szerint NÖVEKVŐ sorrendben (a legközelebbi
  // elöl) — a `from` a nap eleje, hogy a ma reggel kezdődött, még tartó megbeszélés is látszódjon.
  // A „ma" a MEGJELENÍTÉSI zóna (Europe/Budapest) napja — nem a böngészőé: egy más zónájú gépen a böngésző
  // éjfele egy másik pillanat, és a lista órákkal elcsúszna. (A naptár ugyanígy számol: `fromZonedTime`.)
  const timeZone = useTimeZone();
  const from = useMemo(() => {
    const today = formatInTimeZone(new Date(), timeZone, 'yyyy-MM-dd');
    return fromZonedTime(`${today}T00:00:00`, timeZone).toISOString();
  }, [timeZone]);

  // a NEKEM SZÓLÓ kitűzött közlemények — a legutóbb kitűzöttek elöl, legfeljebb `PINNED_LIMIT` (a BFF a kitűzés
  // ideje szerint rendez, a saját archiváltak és a nem nekem szólók nélkül); a többi a „+N további" linken
  const pinnedQ = api.messages.useQuery(
    ['dashboard', 'messages', 'pinned'],
    { query: { pinned: 'only', limit: PINNED_LIMIT } },
    {
      enabled: perms.messages && placed.has('pinnedMessages'),
      queryKey: ['dashboard', 'messages', 'pinned'],
    },
  );
  const pinnedBody = pinnedQ.data?.status === 200 ? pinnedQ.data.body : null;
  const pinnedMessages: DashboardPinnedMessage[] = (pinnedBody?.messages ?? []).flatMap((row) =>
    row.pinnedAt
      ? [
          {
            id: row.id,
            subject: row.subject,
            senderName: row.senderName,
            senderGivenName: row.senderGivenName,
            senderFamilyName: row.senderFamilyName,
            senderAvatarUrl: row.senderAvatarUrl,
            senderTeamColor: row.senderTeamColor,
            pinnedAt: row.pinnedAt,
            readByMe: row.readByMe,
          },
        ]
      : [],
  );
  const pinnedTotal = pinnedBody?.total ?? 0;

  const meetingsQ = api.meetings.useQuery(
    ['dashboard', 'meetings', from],
    { query: { from, order: 'asc', limit: MEETING_LIMIT } },
    { enabled: perms.meetings, queryKey: ['dashboard', 'meetings', from] },
  );

  // JEGYEK — EGY hívás adja az aktív összeset (`groups.in_progress`) és a rám-osztottakat (`mineGroups`).
  const ticketsQ = api.tickets.useQuery(
    ['dashboard', 'tickets'],
    { query: { limit: 1 } },
    { enabled: perms.tickets, queryKey: ['dashboard', 'tickets'] },
  );

  // ESZKÖZ-IGÉNYEK döntésre várva (a `requested` státusz a workflow első lépcsője).
  const assetsQ = api.assets.useQuery(
    ['dashboard', 'assets'],
    { query: { scope: 'request', status: 'requested', limit: 1 } },
    { enabled: perms.assetRequests, queryKey: ['dashboard', 'assets'] },
  );

  // SZÁMLÁK: piszkozat (kiállításra vár) · lejárt (összeggel) · fizetésre váró · e hónapban kiállított.
  const monthStart = useMemo(() => {
    const d = new Date();
    return localDay(new Date(d.getFullYear(), d.getMonth(), 1));
  }, []);
  const draftQ = api.invoices.useQuery(
    ['dashboard', 'invoices', 'draft'],
    { query: { status: 'draft', limit: 1 } },
    { enabled: perms.invoicesManage, queryKey: ['dashboard', 'invoices', 'draft'] },
  );
  const overdueQ = api.invoices.useQuery(
    ['dashboard', 'invoices', 'overdue'],
    { query: { status: 'overdue', limit: MONEY_ROW_LIMIT } },
    { enabled: financePlaced, queryKey: ['dashboard', 'invoices', 'overdue'] },
  );
  const awaitingQ = api.invoices.useQuery(
    ['dashboard', 'invoices', 'issued'],
    { query: { status: 'issued', limit: MONEY_ROW_LIMIT } },
    { enabled: financePlaced, queryKey: ['dashboard', 'invoices', 'issued'] },
  );
  const issuedMonthQ = api.invoices.useQuery(
    ['dashboard', 'invoices', 'month', monthStart],
    { query: { issuedFrom: monthStart, limit: 1 } },
    { enabled: financePlaced, queryKey: ['dashboard', 'invoices', 'month', monthStart] },
  );

  // BEVÉTEL: a vezetői riport stat-blokkja (MRR + változás + 12 hónap trend a sparkline-hoz).
  const revenueQ = api.revenueForecast.useQuery(
    ['dashboard', 'revenue'],
    { query: {} },
    { enabled: perms.revenue && placed.has('kpi.mrr'), queryKey: ['dashboard', 'revenue'] },
  );

  // BANKKÁRTYÁK: a lejárat a soron van (`expiryMonth`/`expiryYear`) — a „60 napon belül lejár" szűrés
  // kliens-oldali, mert a lista-végpontnak nincs (és nem is kell) lejárat-szűrője. A kártya-állomány kicsi.
  const cardsQ = api.paymentCards.useQuery(
    ['dashboard', 'cards'],
    { query: { limit: 200 } },
    { enabled: attentionPlaced && perms.paymentCards, queryKey: ['dashboard', 'cards'] },
  );

  // HAVI USAGE: van-e egyetlen sor is az aktuális hónapra? (Nincs → a lekérés nem futott le.)
  const usageMonth = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${`${d.getMonth() + 1}`.padStart(2, '0')}`;
  }, []);
  const usageQ = api.serviceUsageList.useQuery(
    ['dashboard', 'usage', usageMonth],
    {
      query: {
        year: Number(usageMonth.slice(0, 4)),
        month: Number(usageMonth.slice(5, 7)),
        limit: 1,
      },
    },
    { enabled: attentionPlaced && perms.usageData, queryKey: ['dashboard', 'usage', usageMonth] },
  );

  // FÜGGŐ hozzárendelések, amik hamarosan indulnak (a hozzárendelés-lista figyelem-paneljének ugyanaz a
  // végpontja/szabálya) — csak a darabszám kell, a teljes listát a céllap adja.
  const pendingStartingSoonQ = api.serviceAssignmentAttention.useQuery(
    ['dashboard', 'service-assignments', 'attention'],
    {},
    {
      enabled: attentionPlaced && perms.serviceAssignments,
      queryKey: ['dashboard', 'service-assignments', 'attention'],
    },
  );

  // NYITOTT generálási kihagyások a JELEN hónapban (a számla-generátor kihagyás-naplója) — csak darabszám.
  const generationSkipsQ = api.invoiceGenerationSkips.useQuery(
    ['dashboard', 'invoices', 'generation-skips', usageMonth],
    { query: { month: usageMonth, limit: 1 } },
    {
      enabled: attentionPlaced && perms.invoicesView,
      queryKey: ['dashboard', 'invoices', 'generation-skips', usageMonth],
    },
  );

  const meetings =
    meetingsQ.data?.status === 200
      ? meetingsQ.data.body.meetings.map((m) => ({
          id: m.id,
          title: m.title,
          locationKey: m.locationKey,
          startsAt: m.startsAt,
          endsAt: m.endsAt,
          participants: m.participants.map((p) => ({
            id: p.id,
            name: p.name,
            givenName: p.givenName,
            familyName: p.familyName,
            avatarUrl: p.avatarUrl,
            teamColor: p.teamColor,
            status: p.status,
          })),
        }))
      : null;

  const tickets = ticketsQ.data?.status === 200 ? ticketsQ.data.body : null;
  const assets = assetsQ.data?.status === 200 ? assetsQ.data.body : null;
  const draft = draftQ.data?.status === 200 ? draftQ.data.body : null;
  const overdue = overdueQ.data?.status === 200 ? overdueQ.data.body : null;
  const awaiting = awaitingQ.data?.status === 200 ? awaitingQ.data.body : null;
  const issuedMonth = issuedMonthQ.data?.status === 200 ? issuedMonthQ.data.body : null;
  const revenueBody = revenueQ.data?.status === 200 ? revenueQ.data.body : null;
  const cards = cardsQ.data?.status === 200 ? cardsQ.data.body : null;
  const usage = usageQ.data?.status === 200 ? usageQ.data.body : null;

  // A SAJÁT identity-azonosító a `/session` `sub` claimjéből — ugyanaz a lekérdezés-kulcs, amit a
  // `usePermissions` is használ, tehát nincs extra kör (TanStack Query dedupál).
  const sessionQ = api.session.useQuery(['session'], {});
  const myIdentityId = sessionQ.data?.status === 200 ? sessionQ.data.body.sub : null;

  // RSVP-re VÁRÓ megbeszélések: ahol ÉN vagyok résztvevő, és ÉN még nem válaszoltam (`pending`).
  //
  // ⚠️ A SAJÁT identity-re szűrés NEM finomítás. Korábban a feltétel `participants.some(p => p.status ===
  // 'pending')` volt — tehát BÁRMELYIK résztvevő válaszolatlansága beleszámolt. A „Rám vár” kártya így olyan
  // megbeszélést is számolt, amire ÉN már válaszoltam, csak egy kolléga még nem: a szám felfelé torzított, és
  // a kártya olyan teendőt ígért, ami nem a felülethasználóé. A résztvevő-sor `id`-ja az identity-je, a
  // saját identity pedig a `/session` `sub` claimje.
  //
  // ISMERETLEN SAJÁT IDENTITY (a session még tölt, vagy nem adott 200-at) → **0**, nem a régi, tágabb szám:
  // egy túlzó teendő-szám rosszabb, mint egy hiányzó — a lista maga ott van alatta.
  const rsvpPending = useMemo(() => {
    if (meetings === null || myIdentityId === null) return 0;
    return meetings.filter((m) =>
      m.participants.some((p) => p.id === myIdentityId && p.status === 'pending'),
    ).length;
  }, [meetings, myIdentityId]);

  const workItems = useMemo((): WorkItem[] => {
    const items: WorkItem[] = [];
    if (perms.tickets && tickets !== null) {
      items.push({
        key: 'myTickets',
        // AKTÍV jegyek: a `in_progress` csoport a `open`/`reopened` státuszt is tartalmazza (a „Nyitott" fül
        // kivezetése után — `TICKET_GROUP_STATUSES`), ezért NEM adunk össze két csoportot (duplán számolna).
        count: tickets.mineGroups.in_progress,
        to: '/tickets',
        // a jegyek lap „csak az enyém” kapcsolója az URL-ben `mine=1` (a lap maga is így írja)
        search: { mine: '1' },
      });
    }
    if (perms.meetings && meetings !== null) {
      items.push({ key: 'rsvp', count: rsvpPending, to: '/meetings' });
    }
    if (perms.assetRequests && assets !== null) {
      items.push({
        key: 'assetRequests',
        count: assets.total,
        to: '/assets',
        search: { scope: 'request', status: 'requested' },
      });
    }
    if (perms.invoicesManage && draft !== null) {
      items.push({
        key: 'draftInvoices',
        count: draft.total,
        to: '/invoices',
        search: { status: 'draft' },
      });
    }
    return items;
  }, [perms, tickets, meetings, assets, draft, rsvpPending]);

  /**
   * 60 NAPON BELÜL LEJÁRÓ kártyák. A lejárat HÓNAP-pontos (`expiryMonth`/`expiryYear`), és a kártya a hónap
   * UTOLSÓ napjáig érvényes — ezért a hónap végét hasonlítjuk a határnaphoz. A már lejárt kártya is „figyelmet
   * kér" (nem tűnik el a listából).
   */
  const pendingStartingSoonBody =
    pendingStartingSoonQ.data?.status === 200 ? pendingStartingSoonQ.data.body : null;
  const generationSkipsBody =
    generationSkipsQ.data?.status === 200 ? generationSkipsQ.data.body : null;

  const attention = useMemo((): AttentionData | null => {
    if (!perms.paymentCards && !perms.usageData && !perms.serviceAssignments && !perms.invoicesView)
      return null;
    let expiringCards = 0;
    if (cards !== null) {
      const limit = new Date();
      limit.setDate(limit.getDate() + 60);
      for (const c of cards.cards) {
        const month = Number(c.expiryMonth);
        const year = Number(c.expiryYear);
        if (!Number.isFinite(month) || !Number.isFinite(year)) continue;
        // A hónap utolsó napja = a KÖVETKEZŐ hónap 0. napja.
        const endOfMonth = new Date(year < 100 ? 2000 + year : year, month, 0);
        if (endOfMonth.getTime() <= limit.getTime()) expiringCards += 1;
      }
    }
    return {
      expiringCards,
      usageMissing: perms.usageData && usage !== null && usage.total === 0,
      usageMonth,
      pendingStartingSoon: pendingStartingSoonBody?.pendingStartingSoon.length ?? 0,
      generationSkipsOpen: generationSkipsBody?.total ?? 0,
    };
  }, [
    perms.paymentCards,
    perms.usageData,
    perms.serviceAssignments,
    perms.invoicesView,
    cards,
    usage,
    usageMonth,
    pendingStartingSoonBody,
    generationSkipsBody,
  ]);

  return {
    pinnedMessages,
    pinnedTotal,
    meetings,
    meetingsLoading: perms.meetings && meetingsQ.isLoading,
    workItems,
    workLoading:
      (perms.tickets && ticketsQ.isLoading) ||
      (perms.meetings && meetingsQ.isLoading) ||
      (perms.assetRequests && assetsQ.isLoading) ||
      (perms.invoicesManage && draftQ.isLoading),
    // „Nyitott jegyek" KPI = az AKTÍV halmaz (open + in_progress + reopened) — ugyanaz, amit a lista
    // „Folyamatban" füle mutat, ahová a kártya deep-linkje visz (a szám és a céllista így nem csúszik el).
    openTickets: tickets === null ? null : tickets.groups.in_progress,
    finance:
      overdue === null || awaiting === null
        ? null
        : {
            overdue: { count: overdue.total, totals: sumByCurrency(overdue.invoices) },
            awaiting: { count: awaiting.total, totals: sumByCurrency(awaiting.invoices) },
            issuedThisMonth: issuedMonth?.total ?? 0,
          },
    financeLoading: financePlaced && (overdueQ.isLoading || awaitingQ.isLoading),
    revenue:
      revenueBody === null
        ? null
        : {
            mrr: revenueBody.stats.mrr,
            currency: revenueBody.currency,
            changePct: revenueBody.stats.mrrChangePct,
            trend: revenueBody.charts.mrrTrend,
          },
    revenueLoading: perms.revenue && placed.has('kpi.mrr') && revenueQ.isLoading,
    attention,
    attentionLoading:
      (attentionPlaced && perms.paymentCards && cardsQ.isLoading) ||
      (attentionPlaced && perms.usageData && usageQ.isLoading) ||
      (attentionPlaced && perms.serviceAssignments && pendingStartingSoonQ.isLoading) ||
      (attentionPlaced && perms.invoicesView && generationSkipsQ.isLoading),
  };
}
